import React, { ReactNode, useEffect, useRef, useState } from "react";

import Box from "veneer/Box";
import Paper from "veneer/Paper";
import styled from "veneer/styled";

import {
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_EXPANDED_WIDTH,
} from "cortex/ui/src/constant/app";
import { useNavBarContext } from "cortex/ui/src/navigation/NavBarContext";

interface StickyPageActionsProps {
  children: ReactNode;
}

interface FloatingPaperProps {
  expanded: boolean;
}

const FloatingPaper = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "expanded",
})<FloatingPaperProps>(({ expanded, theme }) => {
  const sidebarWidth = expanded
    ? SIDEBAR_EXPANDED_WIDTH
    : SIDEBAR_COLLAPSED_WIDTH;

  return {
    borderTop: `1px solid ${theme.palette.divider}`,
    bottom: 0,
    boxShadow: theme.shadows[4],
    position: "fixed",
    left: sidebarWidth,
    width: `calc(100% - ${sidebarWidth}px)`,
    padding: theme.spacing(2),
    zIndex: 1100,
  };
});

export function StickyPageActions({ children }: StickyPageActionsProps) {
  const { expanded } = useNavBarContext();
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [showFloating, setShowFloating] = useState(false);

  useEffect(() => {
    const node = anchorRef.current;
    if (!node) {
      return;
    }

    if (typeof IntersectionObserver === "undefined") {
      setShowFloating(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowFloating(!entry.isIntersecting);
      },
      {
        root: null,
        threshold: 0.5,
      },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <>
      <Box ref={anchorRef}>{children}</Box>
      {showFloating && (
        <FloatingPaper expanded={expanded}>{children}</FloatingPaper>
      )}
    </>
  );
}
